/* eslint-disable no-shadow */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import _compact from 'lodash/compact';
import dayjs from '../../../utils/dayjs';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import MLAlert from '../../MLAlert/MLAlert';
import MLButton from '../../MLButton/MLButton';
import MLIcon from '../../MLIcon/MLIcon';
import MLLoading from '../..//MLLoading/MLLoading';
import InstructorGradebookGrid from '../InstructorGradebookGrid/InstructorGradebookGrid';
import SearchGradebook from '../Components/SearchGradebook';
import FilterOptions from '../Components/FilterOptions';
import FilterHeader from '../Components/FilterOptions/FilterHeader';

import ManualAssignmentsMenu from '../Components/ManualAssignmentsMenu';
import s from './InstructorGradebook.module.css';
import { syncReefGrades } from '../../../actions/reef';
import {
  gatewaySyncGradesJobInit,
  gatewaySyncGradesJobUpdate,
} from '../../../actions/basic-integration';
import {
  openIndividualStudentGradebook,
  toggleShowFilterOptionsGradebook,
  updateAlert,
  updateGradebook,
  updateInstructor,
} from '../../../actions/gradebook';

import MessageBanner from '../Components/MessageBanner';
import Pagination from '../Components/Pagination';
import V2Pagination from '../Components/Pagination/V2Pagination';
import PointOrGradeToggleButton from '../Components/PointOrGradeToggleButton';
import SidePanel from '../../SidePanel/SidePanel';
import { getConfigObject } from '../../../utils/config';
import { emptyAlert } from '../../../utils/util';
import { sendAnalyticsEvent } from '../../../actions/sendAnalytics';
import { openGradebook } from '../../../actions/analyticsEvents';
import OptionsMenu from '../Components/OptionsMenu/OptionsMenu';
import FilterButton from '../Components/FilterButton/FilterButton';
import HeaderContainer from '../Components/HeaderContainer/HeaderContainer';
import ReefMenu from '../Components/ReefMenu/ReefMenu';
import ShowRowsMenu from '../Components/ShowRowsMenu/ShowRowsMenu';
import InstructorGradebookV2, {
  GradebookPageSizeProvider,
} from '../InstructorGradebookV2';

class InstructorGradebook extends Component {
  static propTypes = {
    courseId: PropTypes.string.isRequired,
    lmsId: PropTypes.string.isRequired,
    isSectionManager: PropTypes.bool,
    isGatewayEnabled: PropTypes.bool,
    disableManualAssignmentsMenu: PropTypes.bool,
    loading: PropTypes.bool,
    user: PropTypes.object,
    alert: PropTypes.object,
    permissions: PropTypes.object,
    enrolledLength: PropTypes.number,
    rdxGradebookSettings: PropTypes.array,
    showSettings: PropTypes.func,
    showPercent: PropTypes.bool,
    exportMenuOpen: PropTypes.bool,
    reefMenuOpen: PropTypes.bool,
    isGradebookFetching: PropTypes.bool,
    isGradebookReFetching: PropTypes.bool,
    showFilterOptions: PropTypes.bool,
    handleCloseExportMenu: PropTypes.func,
    handleCloseReefMenu: PropTypes.func,
    handleReefMenuClick: PropTypes.func,
    hasInstructorLinkedCourse: PropTypes.bool,
    toggleGradebookExportDialog: PropTypes.func,
    toggleLinkReefDialog: PropTypes.func,
    toggleUnlinkReefDialog: PropTypes.func,
    handleAlertReef: PropTypes.func,
    anchorEl: PropTypes.object,
    pageCount: PropTypes.number,
    dispatch: PropTypes.func,
    searchPhrase: PropTypes.string,
    searchType: PropTypes.string,
    exceptionGroups: PropTypes.array,
    filterTypes: PropTypes.array,
    searchDateFrom: PropTypes.object,
    searchDateTo: PropTypes.object,
    gradebookGridData: PropTypes.shape({
      rows: PropTypes.array.isRequired,
      rowCount: PropTypes.number.isRequired,
      columnCount: PropTypes.number.isRequired,
      leftGridColumnCount: PropTypes.number.isRequired,
    }),
    instructor: PropTypes.object,
    disableEnforceFocus: PropTypes.bool,
    refreshGradebookOnSidePanelUpdate: PropTypes.func,
    hasStudentReport: PropTypes.bool,
    getStudentReportLink: PropTypes.func,
    sortColumn: PropTypes.string,
    sortDirection: PropTypes.string,
    averages: PropTypes.array,
    getGradebook: PropTypes.func,
    getGradebookInstructor: PropTypes.func,
    isGradebookV2Active: PropTypes.bool,
    onToggleGradebookV2: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.gridRef = React.createRef();
    this.filterButtonRef = React.createRef();
    this.state = { filterOptionsTopOffset: 0 };
  }

  componentDidMount() {
    const { dispatch, courseId } = this.props;
    window.addEventListener('resize', this.updateDimensions);
    dispatch(sendAnalyticsEvent(openGradebook(courseId)));
  }

  componentDidUpdate(prevProps) {
    this.checkForIndividualStudentGradebook(prevProps);
    this.restoreFilterButtonFocusOnClose(prevProps);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
    this.filterOptionsResizeObserver?.disconnect();
  }

  checkForIndividualStudentGradebook(prevProps) {
    const searchParams = new URLSearchParams(window.location.search);
    const selectedStudentId = searchParams.get('studentId');

    const { isGradebookFetching: prevFetching, loading: prevLoading } =
      prevProps;
    const { dispatch, isGradebookFetching, loading } = this.props;

    if (!selectedStudentId) {
      return null;
    }

    const wasFetching = prevFetching || prevLoading;
    const doneFetching = wasFetching && !isGradebookFetching && !loading;
    if (doneFetching && selectedStudentId) {
      dispatch(openIndividualStudentGradebook(selectedStudentId));
    }
  }

  updateDimensions = () => {
    this.resizeGradebookHeight(this.gridRef.current);
  };

  restoreFilterButtonFocusOnClose = (prevProps) => {
    const { isGradebookV2Active, showFilterOptions } = this.props;
    if (
      isGradebookV2Active &&
      prevProps.showFilterOptions &&
      !showFilterOptions
    ) {
      this.filterButtonRef.current?.focus();
    }
  };

  async getSyncGradesStatus(jobId, delay) {
    const { dispatch } = this.props;
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const jobData = await dispatch(gatewaySyncGradesJobUpdate(jobId));
          resolve(jobData);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  }

  syncGradebook = async () => {
    const { dispatch } = this.props;
    this.gatewayJobTimeout = 60 * 1000; // 1 minute timeout for gateway grade sync jobs
    this.gatewayJobRetryDelay = 10 * 1000; // 10 second retry delay for checking jobs
    try {
      dispatch(
        updateGradebook({
          isGradeSyncInProgress: true,
        }),
      );
      const gatewayJob = await dispatch(
        gatewaySyncGradesJobInit(this.props.courseId),
      );
      if (gatewayJob.status === 'success' && !gatewayJob.id) {
        // success without a job ID means there were no grades to sync
        dispatch(
          updateAlert({
            show: true,
            type: 'success',
            dismissible: true,
            text: gatewayJob.statusMessage,
          }),
        );
      } else if (gatewayJob.status !== 'pending') {
        throw new Error('An error occurred while attempting to sync grades');
      } else {
        // we have a queued job we need to check on periodically
        const jobTimeout = dayjs().add(this.gatewayJobTimeout, 'ms');
        let timeoutDelay = 10; // very short initial delay
        let jobResults = null;
        while (dayjs().isBefore(jobTimeout)) {
          jobResults = await this.getSyncGradesStatus(
            gatewayJob.id,
            timeoutDelay,
          );
          if (jobResults.status === 'error') {
            throw new Error(
              'An error occurred while attempting to sync grades',
            );
          } else if (jobResults.status === 'complete') {
            break;
          } else {
            timeoutDelay = this.gatewayJobRetryDelay; // standard retry delay
          }
        }
        if (jobResults.status !== 'complete') {
          throw new Error(
            'The grade sync process timed out. Please try again later',
          );
        }
        const failureCount = get(jobResults, 'results.failure_count', 0);
        const failureText = failureCount > 0 ? `${failureCount} failed` : '';
        const successCount = get(jobResults, 'results.success_count', 0);
        const successText = `${successCount} grade${successCount === 1 ? '' : 's'} synced`;
        dispatch(
          updateAlert({
            show: true,
            type: failureCount > 0 ? 'warning' : 'success',
            dismissible: true,
            text: _compact([
              'Grade sync completed',
              successText,
              failureText,
            ]).join('. '),
          }),
        );
      }
    } catch (err) {
      dispatch(
        updateAlert({
          show: true,
          type: 'error',
          dismissible: true,
          text: err.toString(),
        }),
      );
    } finally {
      dispatch(
        updateGradebook({
          isGradeSyncInProgress: false,
        }),
      );
    }
  };

  handleSyncReefGrades = () => {
    const { courseId, user, dispatch, handleAlertReef } = this.props;
    const info = {
      reefCourseId: user.reefCourseId,
    };
    dispatch(syncReefGrades(courseId, info))
      .then(() => {
        const success = {
          type: 'success',
          message: 'Grade sync complete',
        };
        handleAlertReef(success);
      })
      .catch((err) => {
        const warning = {
          type: 'warning',
          message: err.message ? err.message : 'Grade sync failed',
        };
        handleAlertReef(warning);
      });
  };

  handleExportMenuClick = (event) => {
    event.preventDefault();
    const { dispatch } = this.props;
    dispatch(
      updateGradebook({
        open: true,
        anchorEl: event.currentTarget,
      }),
    );
  };

  handleCloseExportMenu = () => {
    const { dispatch } = this.props;
    dispatch(
      updateGradebook({
        open: false,
      }),
    );
  };

  resizeGradebookHeight = () => {
    const { dispatch } = this.props;
    if (this.gridRef) {
      const { bottom } = this.gridRef.getBoundingClientRect();
      const gradebookTableHeight = window.innerHeight - bottom - 60;
      dispatch(updateInstructor({ gradebookTableHeight }));
    }
  };

  refGridCallback = (element) => {
    this.gridRef = element;
    this.resizeGradebookHeight();
  };

  measureFilterOptionsTopOffset = () => {
    if (!this.topAreaNode) {
      return;
    }
    const filterOptionsTopOffset =
      this.topAreaNode.offsetTop + this.topAreaNode.offsetHeight;
    if (filterOptionsTopOffset !== this.state.filterOptionsTopOffset) {
      this.setState({ filterOptionsTopOffset });
    }
  };

  topAreaRefCallback = (element) => {
    if (this.filterOptionsResizeObserver) {
      this.filterOptionsResizeObserver.disconnect();
    }
    this.topAreaNode = element;
    if (!element) {
      return;
    }
    this.measureFilterOptionsTopOffset();
    if (typeof ResizeObserver !== 'undefined') {
      this.filterOptionsResizeObserver = new ResizeObserver(
        this.measureFilterOptionsTopOffset,
      );
      this.filterOptionsResizeObserver.observe(element);
    }
  };

  shouldDisplayFilterHeader() {
    const { exceptionGroups, filterTypes, searchDateFrom, searchDateTo } =
      this.props;
    if (exceptionGroups.length > 0) {
      return true;
    }
    if (filterTypes.length > 0) {
      return true;
    }
    if (searchDateFrom && searchDateTo) {
      return true;
    }
    return false;
  }

  renderLoading = () => (
    <div className={s.spinnerContainer}>
      <MLLoading />
    </div>
  );

  renderAlert() {
    const { alert } = this.props;
    const { dispatch } = this.props;

    return (
      <MLAlert
        show={alert.show}
        dismissTime={alert.dismissTime}
        dismissible={alert.dismissible}
        focusOnShow={alert.focusOnShow ?? false}
        focusDelay={alert.focusDelay ?? 0}
        onDismiss={() => {
          dispatch(
            updateGradebook({
              alert: emptyAlert,
            }),
          );
        }}
        text={alert.text}
        alertType={alert.type}
      />
    );
  }

  renderConnectCourseReef(style) {
    const { toggleLinkReefDialog } = this.props;
    const text = 'Connect to Course';
    const borderTop = '1px solid #DDDDDD';

    return (
      <MenuItem
        onClick={toggleLinkReefDialog}
        style={{
          ...style,
          borderTop,
        }}
      >
        {text}
      </MenuItem>
    );
  }

  renderDisconnectReef(style) {
    const { toggleUnlinkReefDialog } = this.props;

    return (
      <MenuItem onClick={toggleUnlinkReefDialog} style={style}>
        <span data-test-id="iclicker_menu_disconnect">Disconnect iClicker</span>
      </MenuItem>
    );
  }

  renderSyncReefGrades(style) {
    const { user } = this.props;
    if (!user.reefCourseId) {
      return null;
    }
    return (
      <MenuItem
        onClick={this.handleSyncReefGrades}
        style={style}
        data-test-id="iclicker-refresh-grades"
      >
        Refresh iClicker Grades
      </MenuItem>
    );
  }

  renderReefSyncInfo(style) {
    const { user, toggleLinkReefDialog } = this.props;
    const reefEmail = get(user, 'reefProfile.email', null);
    const reefFirstName = get(user, 'reefProfile.firstName', null);
    const reefLastName = get(user, 'reefProfile.lastName', null);
    const reefCourseName = get(user, 'reefCourseName', 'N/A');
    const reefCourseId = get(user, 'reefCourseId');

    return (
      <MenuItem
        data-test-id="iclicker_info"
        style={{
          ...style,
          borderTop: 'none',
          backgroundColor: '#F3F3F3',
          alignItems: 'stretch',
          flexDirection: 'column',
        }}
      >
        <div className={s.reefSyncInfoInnerMenuSection}>
          <div>Account:</div>
          {reefFirstName && reefLastName && (
            <div>
              {reefFirstName} {reefLastName}
            </div>
          )}
          {reefEmail && <div>{reefEmail}</div>}
        </div>
        <div
          className={s.reefSyncInfoInnerMenuSection}
          data-test-id="iclicker_info_link_course"
        >
          <div>Connected To:</div>
          {reefCourseId ? (
            <div>{reefCourseName}</div>
          ) : (
            <div>
              None,&nbsp;
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a role="button" onClick={toggleLinkReefDialog}>
                Link a course now.
              </a>
            </div>
          )}
        </div>
      </MenuItem>
    );
  }

  renderReefMenu() {
    const {
      user,
      permissions,
      anchorEl,
      handleReefMenuClick,
      reefMenuOpen,
      handleCloseReefMenu,
      toggleLinkReefDialog,
      hasInstructorLinkedCourse,
      disableEnforceFocus,
    } = this.props;

    const listStyles = {
      paddingTop: 0,
      paddingBottom: 0,
    };

    const itemStyle = {
      color: '#00758e',
      minHeight: 'fit-content',
      lineHeight: 'normal',
      fontSize: '14px',
      padding: '9px 9px',
      borderTop: '1px solid #DDDDDD',
    };

    const isReefLinked = get(user, 'isReefLinked', false);
    const isReefStatusFetching = get(user, 'isReefStatusFetching');
    const canLinkReefCourse = get(
      permissions,
      'course.reef.linking.course',
      false,
    );
    const canSyncGrades = get(permissions, 'course.reef.gradeSync', false);
    const { ICLICKER_INTEGRATION_DISABLED } = getConfigObject();

    if (
      !ICLICKER_INTEGRATION_DISABLED &&
      canSyncGrades &&
      !isReefStatusFetching
    ) {
      return (
        <div>
          {isReefLinked && (
            <MLButton
              btnType="secondary"
              className={s.iClickerButton}
              onClick={handleReefMenuClick}
              testId="iclicker_menu"
            >
              <MLIcon
                type="iclicker"
                viewBox="0 0 85 85"
                className={s.headerBtnIcon}
              />
              <span data-test-id="iclicker-menu-text">iClicker</span>
              <MLIcon
                type="caret_down"
                className={`${s.headerBtnIcon} ${s.tinyChevronIcon} ${s.iClickerChevron}`}
              />
            </MLButton>
          )}
          {!isReefLinked && canLinkReefCourse && (
            <MLButton
              btnType="secondary"
              testId="sync_iclicker"
              className={s.iClickerButton}
              onClick={toggleLinkReefDialog}
            >
              <MLIcon
                type="iclicker"
                viewBox="0 0 85 85"
                className={s.headerBtnIcon}
              />
              <span data-test-id="iclicker-menu-text">Sync iClicker</span>
            </MLButton>
          )}
          <Popover
            open={reefMenuOpen}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handleCloseReefMenu}
            disableEnforceFocus={disableEnforceFocus}
          >
            <MenuList liststyle={listStyles}>
              {this.renderReefSyncInfo(itemStyle)}
              {!hasInstructorLinkedCourse &&
                this.renderConnectCourseReef(itemStyle)}
              {canSyncGrades && this.renderSyncReefGrades(itemStyle)}
              {isReefLinked &&
                canLinkReefCourse &&
                this.renderDisconnectReef(itemStyle)}
              <a
                href={getConfigObject().REEF_GO_TO_ICLICKER_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={s.linkNoUnderline}
              >
                <MenuItem style={itemStyle}>
                  <span data-test-id="manage-iclicker-menu">
                    Manage iClicker
                  </span>
                </MenuItem>
              </a>
            </MenuList>
          </Popover>
        </div>
      );
    }
    return null;
  }

  renderExportBtn() {
    const {
      anchorEl,
      exportMenuOpen,
      toggleGradebookExportDialog,
      isGatewayEnabled,
      disableEnforceFocus,
    } = this.props;

    const itemStyle = {
      color: '#00758e',
      minHeight: 'fit-content',
      lineHeight: 'normal',
      fontSize: '14px',
      padding: '9px 9px',
    };

    return (
      <div>
        <MLButton
          btnType="secondary"
          testId="sync_and_export"
          onClick={this.handleExportMenuClick}
        >
          <MLIcon type="upload" className={s.headerBtnIcon} />
          <span>Sync & Export</span>
          <MLIcon
            type="caret_down"
            className={`${s.headerBtnIcon} ${s.tinyChevronIcon}`}
          />
        </MLButton>
        <Popover
          open={exportMenuOpen}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={this.handleCloseExportMenu}
          disableEnforceFocus={disableEnforceFocus}
        >
          <MenuList>
            <MenuItem
              onClick={toggleGradebookExportDialog}
              style={itemStyle}
              testid="export-gradebook-button"
            >
              Export Gradebook
            </MenuItem>
            {isGatewayEnabled && (
              <MenuItem onClick={this.syncGradebook} style={itemStyle}>
                Sync with LMS
              </MenuItem>
            )}
          </MenuList>
        </Popover>
      </div>
    );
  }

  renderSettingsBtn() {
    return (
      <MLButton
        btnType="secondary"
        testId="settings"
        onClick={() => this.props.showSettings()}
      >
        <MLIcon className={s.headerBtnIcon} type="gear" />
        <span>Gradebook Settings</span>
      </MLButton>
    );
  }

  renderFilterButton() {
    const { dispatch, showFilterOptions } = this.props;
    const btnType = showFilterOptions ? 'primary' : 'secondary';
    return (
      <MLButton
        ref={this.filterButtonRef}
        btnType={btnType}
        testId="show_filters"
        onClick={() => {
          dispatch(toggleShowFilterOptionsGradebook());
        }}
      >
        <MLIcon
          className={
            showFilterOptions ? s.headerBtnIconSelected : s.headerBtnIcon
          }
          type="list_right"
        />
        <span>Filter {showFilterOptions}</span>
      </MLButton>
    );
  }

  renderEmptyMessageBanner() {
    const { enrolledLength, isGradebookReFetching } = this.props;

    return (
      enrolledLength <= 0 &&
      !isGradebookReFetching && (
        <MessageBanner
          color="blue"
          message="There are no students currently enrolled in your course, you can invite students at any time."
          testId="empty_message_banner"
        />
      )
    );
  }

  renderMessageBanners() {
    const { isSectionManager } = this.props;
    if (isSectionManager) {
      return (
        <MessageBanner
          color="purple"
          message="Students and Grades will not appear in a Section Manager."
          testId="section_manager_message_banner"
        />
      );
    }
    return null;
  }

  renderHeader() {
    const {
      dispatch,
      courseId,
      isSectionManager,
      disableManualAssignmentsMenu,
      pageCount,
      gradebookGridData: { hasStudents, hasAssignments },
      disableEnforceFocus,
      showFilterOptions,
      limit,
      count,
      searchPhrase,
    } = this.props;

    const { isGradebookV2Active } = this.props;
    const { FF_SIDE_PANEL_ACTIVE = false } = getConfigObject();

    // V1: Only show pagination when multiple pages exist
    const hasV1Pagination =
      FF_SIDE_PANEL_ACTIVE && !isSectionManager && pageCount > 1;
    // V2: Always show pagination controls (rows selector should always be accessible)
    const hasV2Pagination =
      FF_SIDE_PANEL_ACTIVE && !isSectionManager && hasStudents;
    const hasPagination = isGradebookV2Active
      ? hasV2Pagination
      : hasV1Pagination;

    return (
      <HeaderContainer
        innerRef={this.refGridCallback}
        hasPagination={hasPagination}
      >
        <div
          className={FF_SIDE_PANEL_ACTIVE ? s.toolbarLeftSide : s.toolbarLeft}
        >
          <SearchGradebook
            courseId={courseId}
            disableEnforceFocus={disableEnforceFocus}
          />
          {FF_SIDE_PANEL_ACTIVE ? (
            <FilterButton
              ref={this.filterButtonRef}
              dispatch={dispatch}
              showFilterOptions={showFilterOptions}
            />
          ) : (
            this.renderFilterButton()
          )}
          {isGradebookV2Active && showFilterOptions && <FilterOptions />}
        </div>
        {hasV2Pagination && isGradebookV2Active && (
          <V2Pagination sendEvent={sendAnalyticsEvent} />
        )}
        {hasV1Pagination && !isGradebookV2Active && (
          <Pagination sendEvent={sendAnalyticsEvent} />
        )}
        <div
          className={FF_SIDE_PANEL_ACTIVE ? s.toolbarRightSide : s.toolbarRight}
        >
          {!FF_SIDE_PANEL_ACTIVE && !isSectionManager && pageCount > 1 && (
            <Pagination sendEvent={sendAnalyticsEvent} />
          )}
          {FF_SIDE_PANEL_ACTIVE && (
            <div className={s.optionsMenuContainer}>
              {isGradebookV2Active && hasStudents && (
                <ShowRowsMenu
                  dispatch={dispatch}
                  courseId={courseId}
                  limit={limit}
                  count={count}
                  searchPhrase={searchPhrase}
                  sortColumn={this.props.sortColumn}
                  sortDirection={this.props.sortDirection}
                  exceptionGroups={this.props.exceptionGroups}
                  filterTypes={this.props.filterTypes}
                  isGradebookV2Active={isGradebookV2Active}
                  getGradebookInstructor={this.props.getGradebookInstructor}
                />
              )}
              <ReefMenu
                user={this.props.user}
                isSectionManager={isSectionManager}
                disableEnforceFocus={disableEnforceFocus}
                permissions={this.props.permissions}
                toggleLinkReefDialog={this.props.toggleLinkReefDialog}
                toggleUnlinkReefDialog={this.props.toggleUnlinkReefDialog}
                handleSyncReefGrades={this.handleSyncReefGrades}
                hasInstructorLinkedCourse={this.props.hasInstructorLinkedCourse}
              />
              <OptionsMenu
                dispatch={dispatch}
                courseId={courseId}
                isSectionManager={isSectionManager}
                disableEnforceFocus={disableEnforceFocus}
                disableManualAssignmentsMenu={disableManualAssignmentsMenu}
                sendEvent={sendAnalyticsEvent}
                showSettings={this.props.showSettings}
                showPercent={this.props.showPercent}
                hasStudents={hasStudents}
                hasAssignments={hasAssignments}
                syncGradebook={this.syncGradebook}
                isGatewayEnabled={this.props.isGatewayEnabled}
                toggleGradebookExportDialog={
                  this.props.toggleGradebookExportDialog
                }
                isGradebookV2Active={this.props.isGradebookV2Active}
                onToggleGradebookV2={this.props.onToggleGradebookV2}
              />
            </div>
          )}

          {!FF_SIDE_PANEL_ACTIVE && (
            <div className={s.btnContainer}>
              {!isSectionManager && hasStudents && (
                <PointOrGradeToggleButton sendEvent={sendAnalyticsEvent} />
              )}
              {!isSectionManager && this.renderExportBtn()}
              {!isSectionManager && (
                <ManualAssignmentsMenu
                  isDisabled={disableManualAssignmentsMenu}
                  sendEvent={sendAnalyticsEvent}
                  dispatch={dispatch}
                  courseId={courseId}
                  disableEnforceFocus={disableEnforceFocus}
                />
              )}
              {this.renderSettingsBtn()}
              {!isSectionManager && this.renderReefMenu()}
            </div>
          )}
        </div>
      </HeaderContainer>
    );
  }

  render() {
    const { isGradebookV2Active } = this.props;

    if (isGradebookV2Active) {
      const {
        alert,
        courseId,
        lmsId,
        showFilterOptions,
        loading,
        isSectionManager,
        isGradebookFetching,
        isGradebookReFetching,
      } = this.props;
      const shouldShowOverlay = showFilterOptions;
      const shouldRenderLoader =
        (isGradebookFetching && !isGradebookReFetching) || loading;
      const { FF_SIDE_PANEL_ACTIVE = false } = getConfigObject();

      return (
        <React.Fragment>
          {alert && alert.show && this.renderAlert()}
          <GradebookPageSizeProvider dispatch={this.props.dispatch}>
            <div className={s.containerSidePanel}>
              <div
                data-test-id="instructor_gradebook_container"
                className={`${s.container} ${s.containerV2}`}
                style={{
                  '--gb-filter-panel-offset-top': `${this.state.filterOptionsTopOffset}px`,
                }}
              >
                <div ref={this.topAreaRefCallback}>
                  {this.renderHeader()}
                  {this.renderMessageBanners()}
                  {!shouldRenderLoader &&
                    !isSectionManager &&
                    this.renderEmptyMessageBanner()}
                </div>
                <div
                  className={s.gradeBookTableWrapper}
                  style={{ flex: 1, minHeight: 0 }}
                >
                  {this.shouldDisplayFilterHeader() && (
                    <FilterHeader innerRef={this.refGridCallback} />
                  )}
                  {shouldShowOverlay && <div className={s.gradebookOverlay} />}
                  <InstructorGradebookV2
                    gradebookGridData={this.props.gradebookGridData}
                    collapsedCategoryIds={this.props.collapsedCategoryIds}
                    showPercent={this.props.showPercent}
                    loading={this.props.loading}
                    isGradebookFetching={this.props.isGradebookFetching}
                    isGradebookReFetching={this.props.isGradebookReFetching}
                    courseId={this.props.courseId}
                    dispatch={this.props.dispatch}
                    pageCount={this.props.pageCount}
                    getNextGradebookPage={this.props.getNextGradebookPage}
                    searchPhrase={this.props.searchPhrase}
                    searchType={this.props.searchType}
                    showGradeOverrideDialog={this.props.showGradeOverrideDialog}
                    hasInstructorLinkedCourse={
                      this.props.hasInstructorLinkedCourse
                    }
                    rdxGradebookSettings={this.props.rdxGradebookSettings}
                    sortColumn={this.props.sortColumn}
                    sortDirection={this.props.sortDirection}
                    hasStudentReport={this.props.hasStudentReport}
                    getStudentReportLink={this.props.getStudentReportLink}
                    getLaunchUrl={this.props.getLaunchUrl}
                    averages={this.props.averages}
                    updateAssignmentAverages={
                      this.props.updateAssignmentAverages
                    }
                    onTabChange={this.props.onTabChange}
                    disableManualAssignmentsMenu={
                      this.props.disableManualAssignmentsMenu
                    }
                    page={this.props.page}
                    limit={this.props.limit}
                    count={this.props.count}
                    getGradebook={this.props.getGradebook}
                    getGradebookInstructor={this.props.getGradebookInstructor}
                    exceptionGroups={this.props.exceptionGroups}
                    filterTypes={this.props.filterTypes}
                  />
                </div>
              </div>
              {FF_SIDE_PANEL_ACTIVE && (
                <SidePanel
                  courseId={courseId}
                  lmsId={lmsId}
                  iamId={this.props.user?.user_id}
                  refreshGradebook={
                    this.props.refreshGradebookOnSidePanelUpdate
                  }
                  hasStudentReport={this.props.hasStudentReport}
                  getStudentReportLink={this.props.getStudentReportLink}
                />
              )}
            </div>
          </GradebookPageSizeProvider>
        </React.Fragment>
      );
    }

    const { gradebookTableHeight, scrollTop, scrollLeft } =
      this.props.instructor;
    const {
      alert,
      loading,
      isSectionManager,
      isGradebookFetching,
      isGradebookReFetching,
      showPercent,
      dispatch,
      showFilterOptions,
      courseId,
      lmsId,
    } = this.props;
    const shouldShowOverlay = showFilterOptions;
    const shouldRenderLoader =
      (isGradebookFetching && !isGradebookReFetching) || loading;

    const { FF_SIDE_PANEL_ACTIVE = false } = getConfigObject();

    return (
      <React.Fragment>
        {alert && alert.show && this.renderAlert()}
        <div className={s.containerSidePanel}>
          <div
            data-test-id="instructor_gradebook_container"
            className={s.container}
          >
            {this.renderHeader()}
            {this.renderMessageBanners()}
            {!shouldRenderLoader &&
              !isSectionManager &&
              this.renderEmptyMessageBanner()}
            <div className={s.gradeBookTableWrapper}>
              {showFilterOptions && <FilterOptions />}
              {this.shouldDisplayFilterHeader() && (
                <FilterHeader innerRef={this.refGridCallback} />
              )}
              {shouldShowOverlay && <div className={s.gradebookOverlay} />}
              {shouldRenderLoader ? (
                this.renderLoading()
              ) : (
                /* eslint-disable-next-line no-shadow */
                <InstructorGradebookGrid
                  {...this.props}
                  data-test-id="instructor_gradebook_grid"
                  scrollLeft={scrollLeft}
                  scrollTop={scrollTop}
                  showPercent={showPercent}
                  gradebookTableHeight={gradebookTableHeight}
                  updateCell={(cell) => {
                    dispatch(
                      updateInstructor({
                        gradebook: cell,
                      }),
                    );
                  }}
                  updateScrollPosition={({ scrollTop, scrollLeft }) => {
                    dispatch(
                      updateInstructor({
                        scrollLeft,
                        scrollTop,
                      }),
                    );
                  }}
                />
              )}
            </div>
          </div>
          {FF_SIDE_PANEL_ACTIVE && (
            <SidePanel
              courseId={courseId}
              lmsId={lmsId}
              iamId={this.props.user?.user_id}
              refreshGradebook={this.props.refreshGradebookOnSidePanelUpdate}
              hasStudentReport={this.props.hasStudentReport}
              getStudentReportLink={this.props.getStudentReportLink}
            />
          )}
        </div>
      </React.Fragment>
    );
  }
}

export default InstructorGradebook;
